import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tournamentAPI, playerAPI, dataAPI } from '../services/api';
import ColumnMappingModal from '../components/ColumnMappingModal';
import './TournamentSetup.css';

function TournamentDataImport() {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [playerFile, setPlayerFile] = useState(null);
  const [meritFile, setMeritFile] = useState(null);
  const [paceFile, setPaceFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  const [importedData, setImportedData] = useState({
    players: [],
    merit: [],
    pace: []
  });
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => {
    loadTournament();
  }, [tournamentId]);

  const loadTournament = async () => {
    try {
      const response = await tournamentAPI.getById(tournamentId);
      setTournament(response.data);
    } catch (error) {
      setError('Failed to load tournament information');
    }
  };

  const handleFileUpload = async () => {
    let combinedDataDetected = false;
    const newImportedData = { players: [], merit: [], pace: [] };
    
    if (playerFile) {
      try {
        setUploadStatus(prev => ({ ...prev, players: 'uploading' }));
        const result = await playerAPI.bulkImport(tournamentId, playerFile);
        setUploadStatus(prev => ({ ...prev, players: 'success' }));
        
        newImportedData.players = result.data.players || [];
        newImportedData.validation = result.data.validation || null;
        
        // Check if combined format was detected
        if (result.data.meritData || result.data.paceData) {
          combinedDataDetected = true;
          console.log('Combined format detected:', {
            players: result.data.players?.length || 0,
            merit: result.data.meritData?.length || 0,
            pace: result.data.paceData?.length || 0
          });
          
          newImportedData.merit = result.data.meritData || [];
          newImportedData.pace = result.data.paceData || [];
          
          // Update status to show what was extracted
          if (result.data.meritData?.length > 0) {
            setUploadStatus(prev => ({ ...prev, merit: 'success' }));
          }
          if (result.data.paceData?.length > 0) {
            setUploadStatus(prev => ({ ...prev, pace: 'success' }));
          }
        }
        
        // Log validation results
        if (result.data.validation) {
          console.log('Data validation results:', result.data.validation);
        }
      } catch (error) {
        console.error('Error uploading players:', error);
        setUploadStatus(prev => ({ ...prev, players: 'error' }));
        throw new Error(`Failed to upload player data: ${error.message}`);
      }
    }
    
    // Only upload separate merit/pace files if not already included in combined format
    if (!combinedDataDetected) {
      if (meritFile) {
        try {
          setUploadStatus(prev => ({ ...prev, merit: 'uploading' }));
          await dataAPI.uploadMerit(tournamentId, meritFile);
          setUploadStatus(prev => ({ ...prev, merit: 'success' }));
        } catch (error) {
          console.error('Error uploading merit data:', error);
          setUploadStatus(prev => ({ ...prev, merit: 'error' }));
          throw new Error(`Failed to upload merit data: ${error.message}`);
        }
      }
      
      if (paceFile) {
        try {
          setUploadStatus(prev => ({ ...prev, pace: 'uploading' }));
          await dataAPI.uploadPace(tournamentId, paceFile);
          setUploadStatus(prev => ({ ...prev, pace: 'success' }));
        } catch (error) {
          console.error('Error uploading pace data:', error);
          setUploadStatus(prev => ({ ...prev, pace: 'error' }));
          throw new Error(`Failed to upload pace data: ${error.message}`);
        }
      }
    }
    
    setImportedData(newImportedData);
  };

  const handleImport = async () => {
    if (!playerFile && !meritFile && !paceFile) {
      setError('Please select at least one file to import');
      return;
    }

    // If there's a player file, show the column mapping modal first
    if (playerFile) {
      try {
        setIsLoading(true);
        setError(null);

        // Get preview data for column mapping
        const previewResponse = await playerAPI.previewExcel(playerFile);
        setPreviewData(previewResponse.data);
        setPendingFile(playerFile);
        setShowMappingModal(true);
      } catch (error) {
        console.error('Error previewing file:', error);
        setError(error.response?.data?.message || 'Failed to preview file. Please check the file format.');
      } finally {
        setIsLoading(false);
      }
    } else {
      // No player file, just upload merit/pace files directly
      try {
        setIsLoading(true);
        setError(null);
        await handleFileUpload();
      } catch (error) {
        console.error('Error importing data:', error);
        setError(error.message || 'Failed to import data');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMappingConfirm = async (columnMapping) => {
    try {
      setIsLoading(true);
      setError(null);

      // Import with the user's column mapping
      setUploadStatus(prev => ({ ...prev, players: 'uploading' }));
      const result = await playerAPI.importWithMapping(tournamentId, pendingFile, columnMapping);
      setUploadStatus(prev => ({ ...prev, players: 'success' }));

      const newImportedData = {
        players: result.data.players || [],
        merit: [],
        pace: [],
        validation: result.data.validation || null
      };

      // Check if combined format was detected
      if (result.data.meritData || result.data.paceData) {
        console.log('Combined format detected:', {
          players: result.data.players?.length || 0,
          merit: result.data.meritData?.length || 0,
          pace: result.data.paceData?.length || 0
        });

        newImportedData.merit = result.data.meritData || [];
        newImportedData.pace = result.data.paceData || [];

        if (result.data.meritData?.length > 0) {
          setUploadStatus(prev => ({ ...prev, merit: 'success' }));
        }
        if (result.data.paceData?.length > 0) {
          setUploadStatus(prev => ({ ...prev, pace: 'success' }));
        }
      }

      // Handle separate merit/pace files if provided
      if (meritFile) {
        try {
          setUploadStatus(prev => ({ ...prev, merit: 'uploading' }));
          await dataAPI.uploadMerit(tournamentId, meritFile);
          setUploadStatus(prev => ({ ...prev, merit: 'success' }));
        } catch (error) {
          console.error('Error uploading merit data:', error);
          setUploadStatus(prev => ({ ...prev, merit: 'error' }));
        }
      }

      if (paceFile) {
        try {
          setUploadStatus(prev => ({ ...prev, pace: 'uploading' }));
          await dataAPI.uploadPace(tournamentId, paceFile);
          setUploadStatus(prev => ({ ...prev, pace: 'success' }));
        } catch (error) {
          console.error('Error uploading pace data:', error);
          setUploadStatus(prev => ({ ...prev, pace: 'error' }));
        }
      }

      setImportedData(newImportedData);
      setShowMappingModal(false);
      setPendingFile(null);
      setPreviewData(null);

      if (result.data.validation) {
        console.log('Data validation results:', result.data.validation);
      }
    } catch (error) {
      console.error('Error importing with mapping:', error);
      setError(error.response?.data?.message || error.message || 'Failed to import data');
      setUploadStatus(prev => ({ ...prev, players: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMappingCancel = () => {
    setShowMappingModal(false);
    setPendingFile(null);
    setPreviewData(null);
  };

  const handleContinue = () => {
    navigate(`/editor/${tournamentId}`);
  };

  const handleSkip = () => {
    navigate(`/editor/${tournamentId}`);
  };

  if (!tournament) {
    return <div>Loading tournament information...</div>;
  }

  return (
    <div className="tournament-setup">
      <div className="setup-container">
        <header className="setup-header">
          <h1>{tournament.name}</h1>
          <p>Step 2: Import tournament data</p>
        </header>

        <div className="setup-form">
          <div className="upload-section">
            <h3>Data Import</h3>
            <p className="upload-note">
              📄 <strong>Upload your Excel file with player data</strong><br/>
              💡 <em>Tip: If your file includes OOM-Rank and POP-Rank columns, they'll be automatically detected!</em>
            </p>

            <div className="upload-grid">
              <div className="upload-item combined-format">
                <label htmlFor="playerFile">
                  <strong>🎯 Player Data (Combined Format Supported)</strong>
                  <span className="file-hint">Required: GHIN, First Name, Last Name, Handicap Index</span>
                  <span className="file-hint">Optional: Pairing, OOM-Rank, POP-Rank</span>
                </label>
                <input
                  type="file"
                  id="playerFile"
                  accept=".xlsx,.xls"
                  onChange={(e) => setPlayerFile(e.target.files[0])}
                />
                {uploadStatus.players && (
                  <span className={`upload-status ${uploadStatus.players}`}>
                    {uploadStatus.players === 'uploading' && '⏳ Uploading...'}
                    {uploadStatus.players === 'success' && '✅ Uploaded'}
                    {uploadStatus.players === 'error' && '❌ Failed'}
                  </span>
                )}
              </div>

              <div className="upload-item">
                <label htmlFor="meritFile">
                  <strong>Merit Rankings</strong>
                  <span className="file-hint">GHIN, OOM-Rank (Order of Merit)</span>
                </label>
                <input
                  type="file"
                  id="meritFile"
                  accept=".xlsx,.xls"
                  onChange={(e) => setMeritFile(e.target.files[0])}
                />
                {uploadStatus.merit && (
                  <span className={`upload-status ${uploadStatus.merit}`}>
                    {uploadStatus.merit === 'uploading' && '⏳ Uploading...'}
                    {uploadStatus.merit === 'success' && '✅ Uploaded'}
                    {uploadStatus.merit === 'error' && '❌ Failed'}
                  </span>
                )}
              </div>

              <div className="upload-item">
                <label htmlFor="paceFile">
                  <strong>Pace of Play</strong>
                  <span className="file-hint">GHIN, POP-Rank (Pace ranking)</span>
                </label>
                <input
                  type="file"
                  id="paceFile"
                  accept=".xlsx,.xls"
                  onChange={(e) => setPaceFile(e.target.files[0])}
                />
                {uploadStatus.pace && (
                  <span className={`upload-status ${uploadStatus.pace}`}>
                    {uploadStatus.pace === 'uploading' && '⏳ Uploading...'}
                    {uploadStatus.pace === 'success' && '✅ Uploaded'}
                    {uploadStatus.pace === 'error' && '❌ Failed'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Import Button */}
          {(playerFile || meritFile || paceFile) && !importedData.players.length && (
            <div className="form-actions">
              <button 
                type="button"
                onClick={handleImport}
                disabled={isLoading}
                className="btn btn-primary"
              >
                {isLoading ? '⏳ Importing...' : 'Import Data'}
              </button>
            </div>
          )}

          {/* Data Summary */}
          {importedData.players.length > 0 && (
            <div className="import-summary">
              <h3>✅ Import Summary</h3>
              <div className="summary-stats">
                <div className="stat-item">
                  <strong>{importedData.players.length}</strong> players imported
                </div>
                {importedData.merit.length > 0 && (
                  <div className="stat-item">
                    <strong>{importedData.merit.length}</strong> merit rankings
                  </div>
                )}
                {importedData.pace.length > 0 && (
                  <div className="stat-item">
                    <strong>{importedData.pace.length}</strong> pace rankings
                  </div>
                )}
              </div>
              
              {/* Validation Results */}
              {importedData.validation && (
                <div className="validation-results">
                  <h4>📊 Data Analysis</h4>
                  <div className="validation-grid">
                    <div className="validation-item">
                      <strong>Players with Handicap:</strong> {importedData.validation.players.withHandicap}/{importedData.validation.players.total}
                    </div>
                    <div className="validation-item">
                      <strong>Time Requests:</strong> {importedData.validation.players.withTimeRequest}
                    </div>
                    <div className="validation-item">
                      <strong>Partner Requests:</strong> {importedData.validation.players.withPartnerRequest}
                    </div>
                    <div className="validation-item">
                      <strong>Merit Coverage:</strong> {importedData.validation.meritData.coverage}%
                    </div>
                    <div className="validation-item">
                      <strong>Pace Coverage:</strong> {importedData.validation.paceData.coverage}%
                    </div>
                  </div>
                  
                  {importedData.validation.recommendations.length > 0 && (
                    <div className="recommendations">
                      <h5>⚠️ Recommendations:</h5>
                      <ul>
                        {importedData.validation.recommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            {importedData.players.length > 0 ? (
              <button 
                type="button" 
                onClick={handleContinue}
                className="btn btn-primary"
              >
                Continue to Tee Sheet Editor
              </button>
            ) : (
              <button 
                type="button" 
                onClick={handleSkip}
                className="btn btn-outline"
              >
                Skip Import (Add Data Later)
              </button>
            )}
            
            <button 
              type="button" 
              onClick={() => navigate('/')}
              className="btn btn-outline"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="excel-format-info">
          <h3>📋 Excel File Formats</h3>
          <div className="format-examples">
            <div className="format-example highlighted">
              <h4>🎯 Combined Format (Recommended - All in One File):</h4>
              <code>GHIN | First Name | Last Name | Handicap Index | Pairing | OOM-Rank | POP-Rank</code>
              <small>Your file format! Merit and pace data will be automatically extracted.</small>
            </div>
            <div className="format-example">
              <h4>Separate Player File:</h4>
              <code>GHIN | First Name | Last Name | Handicap Index | Pairing</code>
            </div>
            <div className="format-example">
              <h4>Separate Merit File:</h4>
              <code>GHIN | OOM-Rank</code>
            </div>
            <div className="format-example">
              <h4>Separate Pace File:</h4>
              <code>GHIN | POP-Rank</code>
            </div>
          </div>
        </div>
      </div>

      {/* Column Mapping Modal */}
      <ColumnMappingModal
        isOpen={showMappingModal}
        onClose={handleMappingCancel}
        previewData={previewData}
        onConfirm={handleMappingConfirm}
        isLoading={isLoading}
      />
    </div>
  );
}

export default TournamentDataImport;